import { parseWorkspaceFile, getExtension, getWorkspaceFileSupport, DOCUMENT_MODEL_VERSION } from './workspaceParsers';
import { generateWorkspaceBlob } from './workspaceGenerators';
import { executeWorkspaceCode } from './workspaceEval';

const DB_NAME = 'ai_workspace_db';
const DB_VERSION = 1;
const FILES_STORE = 'workspace_files';
const BLOBS_STORE = 'workspace_blobs';
const INDEXES_STORE = 'workspace_indexes';
const MAX_FILE_BYTES = 50 * 1024 * 1024;
const MAX_WORKSPACE_BYTES = 500 * 1024 * 1024;
const MAX_TOOL_RESULT_CHARS = 1024 * 1024;
const MAX_SEARCH_RESULTS = 50;
const MAX_TOOL_FILES = 6;
const MAX_EVAL_TEXT_CHARS = 2 * 1024 * 1024;
const MAX_EVAL_CELLS = 1000000;
const MAX_EVAL_ROWS = 100000;
const MAX_EVAL_COLUMNS = 200;
const WORKSPACE_INTENT_PATTERN = /(?:工作区|workspace|(?:分析|统计|清洗|筛选|提取|比较|合并|转换|生成|创建|制作|导出|保存)(?:.{0,20})(?:文件|文档|表格|数据|excel|word|xlsx|docx|markdown|csv|代码)|(?:文件|文档|表格|数据|excel|word|xlsx|docx|markdown|csv|代码)(?:.{0,20})(?:分析|统计|清洗|筛选|提取|比较|合并|转换|生成|创建|制作|导出|保存))/i;

function requestResult(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function transactionDone(transaction) {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error || new Error('IndexedDB transaction aborted'));
  });
}

function createId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return `f_${crypto.randomUUID()}`;
  return `f_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
}

function sanitizeFileName(name = 'file') {
  const cleaned = String(name)
    .replace(/[\\/:*?"<>|\u0000-\u001f]/g, '_')
    .replace(/\.\.+/g, '.')
    .trim();
  return (cleaned || 'file').slice(0, 180);
}

function sessionWorkspaceId(sessionId) {
  return `session:${String(sessionId || 'default')}`;
}

function emitWorkspaceChange(detail = {}) {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('aihelp-workspace-changed', { detail }));
  }
}

async function sha256(blob) {
  if (typeof crypto === 'undefined' || !crypto.subtle) return '';
  const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
  return Array.from(new Uint8Array(digest)).map(byte => byte.toString(16).padStart(2, '0')).join('');
}

class WorkspaceService {
  constructor() {
    this.db = null;
    this.ready = this.open();
    this.persistRequested = false;
  }

  open() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(FILES_STORE)) {
          const store = db.createObjectStore(FILES_STORE, { keyPath: 'id' });
          store.createIndex('workspaceId', 'workspaceId', { unique: false });
          store.createIndex('sha256', 'sha256', { unique: false });
        }
        if (!db.objectStoreNames.contains(BLOBS_STORE)) db.createObjectStore(BLOBS_STORE, { keyPath: 'id' });
        if (!db.objectStoreNames.contains(INDEXES_STORE)) db.createObjectStore(INDEXES_STORE, { keyPath: 'id' });
      };
    });
  }

  async requestPersistence() {
    if (this.persistRequested) return;
    this.persistRequested = true;
    try {
      if (navigator.storage && navigator.storage.persist) await navigator.storage.persist();
    } catch (error) {
      console.warn('Workspace persistent storage request failed:', error);
    }
  }

  async getFile(id) {
    await this.ready;
    return requestResult(this.db.transaction(FILES_STORE, 'readonly').objectStore(FILES_STORE).get(id));
  }

  async getAccessibleFile(id, sessionId) {
    const meta = await this.getFile(id);
    if (!meta) throw new Error('文件不存在。');
    const allowedWorkspaceIds = new Set(['global', sessionWorkspaceId(sessionId)]);
    if (!allowedWorkspaceIds.has(meta.workspaceId)) {
      throw new Error('当前会话无权访问该工作区文件。');
    }
    return meta;
  }

  async getBlob(id) {
    await this.ready;
    const record = await requestResult(this.db.transaction(BLOBS_STORE, 'readonly').objectStore(BLOBS_STORE).get(id));
    return record ? record.blob : null;
  }

  async getIndexes(id) {
    await this.ready;
    const indexes = await requestResult(this.db.transaction(INDEXES_STORE, 'readonly').objectStore(INDEXES_STORE).get(id));
    if (!indexes || indexes.documentModelVersion === DOCUMENT_MODEL_VERSION) return indexes;
    const meta = await this.getFile(id);
    const blob = await this.getBlob(id);
    if (!meta || !blob) return indexes;
    const file = new File([blob], meta.name, { type: meta.mime });
    const parsed = await parseWorkspaceFile(file, { fileId: id });
    const updatedMeta = {
      ...meta,
      documentModelVersion: parsed.documentModelVersion || DOCUMENT_MODEL_VERSION,
      parser: parsed.parser,
      parserVersion: parsed.parserVersion,
      kind: parsed.kind,
      preview: parsed.preview,
      stats: parsed.stats,
      capabilities: parsed.capabilities,
      updatedAt: Date.now()
    };
    const transaction = this.db.transaction([FILES_STORE, INDEXES_STORE], 'readwrite');
    transaction.objectStore(FILES_STORE).put(updatedMeta);
    transaction.objectStore(INDEXES_STORE).put({ id, documentModelVersion: parsed.documentModelVersion || DOCUMENT_MODEL_VERSION, ...parsed.indexes });
    await transactionDone(transaction);
    emitWorkspaceChange({ id, action: 'reparsed' });
    return { id, documentModelVersion: parsed.documentModelVersion || DOCUMENT_MODEL_VERSION, ...parsed.indexes };
  }

  async listFiles(sessionId) {
    await this.ready;
    const files = await requestResult(this.db.transaction(FILES_STORE, 'readonly').objectStore(FILES_STORE).getAll());
    const localId = sessionWorkspaceId(sessionId);
    return (files || [])
      .filter(file => file.workspaceId === localId || file.workspaceId === 'global')
      .sort((a, b) => b.updatedAt - a.updatedAt);
  }

  async getWorkspaceSize(sessionId) {
    const files = await this.listFiles(sessionId);
    return files.reduce((total, file) => total + (file.size || 0), 0);
  }

  async addFile(file, { sessionId, source = 'user', pinned = false, onProgress, sourceFileIds = [], operation = '' } = {}) {
    if (!(file instanceof Blob)) throw new Error('无效文件。');
    if (file.size > MAX_FILE_BYTES) throw new Error('单个工作区文件不能超过 50 MB。');
    const support = getWorkspaceFileSupport(file);
    if (!support.supported) throw new Error(support.reason);
    if ((await this.getWorkspaceSize(sessionId)) + file.size > MAX_WORKSPACE_BYTES) {
      throw new Error('当前工作区已超过 500 MB 容量限制。');
    }

    await this.requestPersistence();
    const now = Date.now();
    const id = createId();
    const name = sanitizeFileName(file.name || 'file');
    const existingFiles = await this.listFiles(sessionId);
    const version = existingFiles.filter(item => item.name === name).length + 1;
    const meta = {
      id,
      workspaceId: pinned ? 'global' : sessionWorkspaceId(sessionId),
      name,
      mime: file.type || 'application/octet-stream',
      extension: getExtension(name),
      size: file.size,
      sha256: '',
      source,
      sourceFileIds: Array.isArray(sourceFileIds) ? sourceFileIds.slice(0, MAX_TOOL_FILES) : [],
      operation: String(operation || '').slice(0, 80),
      version,
      status: 'parsing',
      parser: '',
      parserVersion: '',
      documentModelVersion: DOCUMENT_MODEL_VERSION,
      kind: support.kind,
      preview: '',
      stats: {},
      capabilities: [],
      pinned,
      createdAt: now,
      updatedAt: now,
      error: ''
    };

    await this.ready;
    let transaction;
    try {
      transaction = this.db.transaction([FILES_STORE, BLOBS_STORE], 'readwrite');
      transaction.objectStore(FILES_STORE).put(meta);
      transaction.objectStore(BLOBS_STORE).put({ id, blob: file });
      await transactionDone(transaction);
    } catch (error) {
      if (error && (error.name === 'QuotaExceededError' || /quota|storage/i.test(error.message || ''))) {
        throw new Error('浏览器可用存储空间不足，请删除工作区中的文件后重试。');
      }
      throw error;
    }
    emitWorkspaceChange({ id, action: 'added' });

    try {
      const [hash, parsed] = await Promise.all([
        sha256(file),
        parseWorkspaceFile(file, { fileId: id, onProgress })
      ]);
      Object.assign(meta, {
        sha256: hash,
        status: 'ready',
        parser: parsed.parser,
        parserVersion: parsed.parserVersion,
        documentModelVersion: parsed.documentModelVersion || DOCUMENT_MODEL_VERSION,
        kind: parsed.kind,
        preview: parsed.preview,
        stats: parsed.stats,
        capabilities: parsed.capabilities,
        updatedAt: Date.now()
      });
      transaction = this.db.transaction([FILES_STORE, INDEXES_STORE], 'readwrite');
      transaction.objectStore(FILES_STORE).put(meta);
      transaction.objectStore(INDEXES_STORE).put({ id, documentModelVersion: parsed.documentModelVersion || DOCUMENT_MODEL_VERSION, ...parsed.indexes });
      await transactionDone(transaction);
    } catch (error) {
      meta.status = 'failed';
      meta.error = error && error.message ? error.message : '文件解析失败';
      meta.updatedAt = Date.now();
      await requestResult(this.db.transaction(FILES_STORE, 'readwrite').objectStore(FILES_STORE).put(meta));
    }
    emitWorkspaceChange({ id, action: 'parsed' });
    return meta;
  }

  async retryFile(id, onProgress) {
    const meta = await this.getFile(id);
    const blob = await this.getBlob(id);
    if (!meta || !blob) throw new Error('文件不存在。');
    const file = new File([blob], meta.name, { type: meta.mime });
    const parsed = await parseWorkspaceFile(file, { fileId: id, onProgress });
    Object.assign(meta, {
      status: 'ready', parser: parsed.parser, parserVersion: parsed.parserVersion,
      documentModelVersion: parsed.documentModelVersion || DOCUMENT_MODEL_VERSION,
      kind: parsed.kind, preview: parsed.preview, stats: parsed.stats,
      capabilities: parsed.capabilities, error: '', updatedAt: Date.now()
    });
    const transaction = this.db.transaction([FILES_STORE, INDEXES_STORE], 'readwrite');
    transaction.objectStore(FILES_STORE).put(meta);
    transaction.objectStore(INDEXES_STORE).put({ id, documentModelVersion: parsed.documentModelVersion || DOCUMENT_MODEL_VERSION, ...parsed.indexes });
    await transactionDone(transaction);
    emitWorkspaceChange({ id, action: 'parsed' });
    return meta;
  }

  async deleteFile(id) {
    await this.ready;
    const transaction = this.db.transaction([FILES_STORE, BLOBS_STORE, INDEXES_STORE], 'readwrite');
    transaction.objectStore(FILES_STORE).delete(id);
    transaction.objectStore(BLOBS_STORE).delete(id);
    transaction.objectStore(INDEXES_STORE).delete(id);
    await transactionDone(transaction);
    emitWorkspaceChange({ id, action: 'deleted' });
  }

  async deleteSessionFiles(sessionId) {
    const files = await this.listFiles(sessionId);
    await Promise.all(files.filter(file => file.workspaceId === sessionWorkspaceId(sessionId)).map(file => this.deleteFile(file.id)));
  }

  async setPinned(id, pinned, sessionId) {
    const meta = await this.getFile(id);
    if (!meta) throw new Error('文件不存在。');
    meta.pinned = !!pinned;
    meta.workspaceId = pinned ? 'global' : sessionWorkspaceId(sessionId);
    meta.updatedAt = Date.now();
    await requestResult(this.db.transaction(FILES_STORE, 'readwrite').objectStore(FILES_STORE).put(meta));
    emitWorkspaceChange({ id, action: 'pinned' });
    return meta;
  }

  async readFile({ file_id: id, mode = 'lines', start = 0, limit = 100, sheet, range } = {}, context = {}) {
    const meta = context.sessionId
      ? await this.getAccessibleFile(id, context.sessionId)
      : await this.getFile(id);
    const indexes = await this.getIndexes(id);
    if (!meta || !indexes) throw new Error('文件不存在或尚未解析。');
    if (meta.status !== 'ready') throw new Error(`文件当前状态为 ${meta.status}。`);
    const safeLimit = Math.max(1, Math.min(Number(limit) || 100, 2000));
    const safeStart = Math.max(0, Number(start) || 0);

    if (mode === 'sheet') {
      const target = (indexes.sheets || []).find(item => item.name === sheet) || (indexes.sheets || [])[0];
      if (!target) throw new Error('工作簿中没有可读取的 Sheet。');
      const rows = this.readSheetRange(target.rows, range, safeStart, safeLimit);
      return this.limitToolResult(JSON.stringify({ file_id: id, sheet: target.name, rows }));
    }
    if (mode === 'pages' && Array.isArray(indexes.pages)) {
      return this.limitToolResult(indexes.pages.slice(safeStart, safeStart + safeLimit).map((page, index) => `[Page ${safeStart + index + 1}]\n${page}`).join('\n\n'));
    }
    const text = String(indexes.text || '');
    const offsets = indexes.lineOffsets instanceof Uint32Array
      ? indexes.lineOffsets
      : new Uint32Array(indexes.lineOffsets || [0]);
    const endLine = Math.min(offsets.length, safeStart + safeLimit);
    const lines = [];
    for (let lineIndex = safeStart; lineIndex < endLine; lineIndex += 1) {
      const from = offsets[lineIndex];
      const to = lineIndex + 1 < offsets.length ? offsets[lineIndex + 1] - 1 : text.length;
      lines.push(`${lineIndex + 1}: ${text.slice(from, to)}`);
    }
    return this.limitToolResult(lines.join('\n'));
  }

  readSheetRange(rows, range, start, limit) {
    if (!range) return rows.slice(start, start + limit);
    const match = /^([A-Z]+)(\d+):([A-Z]+)(\d+)$/i.exec(String(range).trim());
    if (!match) throw new Error('表格范围必须使用 A1:F50 格式。');
    const column = letters => letters.toUpperCase().split('').reduce((value, letter) => value * 26 + letter.charCodeAt(0) - 64, 0) - 1;
    const startColumn = column(match[1]);
    const startRow = Math.max(0, Number(match[2]) - 1);
    const endColumn = column(match[3]);
    const endRow = Math.min(rows.length - 1, Number(match[4]) - 1, startRow + limit - 1);
    return rows.slice(startRow, endRow + 1).map(row => row.slice(startColumn, endColumn + 1));
  }

  async search({ file_id: id, query, regex = false, max_results = 50 } = {}, context = {}) {
    if (!query) throw new Error('搜索词不能为空。');
    if (context.sessionId) await this.getAccessibleFile(id, context.sessionId);
    const indexes = await this.getIndexes(id);
    if (!indexes || typeof indexes.text !== 'string') throw new Error('该文件没有可搜索文本。');
    const maxResults = Math.max(1, Math.min(Number(max_results) || 20, MAX_SEARCH_RESULTS));
    let matcher;
    if (regex) {
      if (String(query).length > 200) throw new Error('正则表达式过长。');
      matcher = new RegExp(query, 'i');
    }
    const normalized = String(query).toLowerCase();
    const results = [];
    const offsets = indexes.lineOffsets instanceof Uint32Array
      ? indexes.lineOffsets
      : new Uint32Array(indexes.lineOffsets || [0]);
    for (let index = 0; index < offsets.length && results.length < maxResults; index += 1) {
      const from = offsets[index];
      const to = index + 1 < offsets.length ? offsets[index + 1] - 1 : indexes.text.length;
      const line = indexes.text.slice(from, to);
      const searchableLine = line.slice(0, 10000);
      const matched = matcher ? matcher.test(searchableLine) : searchableLine.toLowerCase().includes(normalized);
      if (matched) results.push({ line: index + 1, text: line.slice(0, 500) });
    }
    return this.limitToolResult(JSON.stringify({ file_id: id, query, results, truncated: results.length === maxResults }));
  }

  async resolveToolFiles(fileIds, context = {}) {
    const ids = Array.isArray(fileIds) ? fileIds.filter(Boolean).map(String) : [String(fileIds || '')];
    const uniqueIds = [...new Set(ids)].filter(Boolean);
    if (!uniqueIds.length) throw new Error('至少需要一个工作区文件。');
    if (uniqueIds.length > MAX_TOOL_FILES) throw new Error(`一次最多处理 ${MAX_TOOL_FILES} 个工作区文件。`);
    return Promise.all(uniqueIds.map(async id => {
      const meta = context.sessionId
        ? await this.getAccessibleFile(id, context.sessionId)
        : await this.getFile(id);
      const indexes = await this.getIndexes(id);
      if (!meta || !indexes) throw new Error(`文件 ${id} 不存在或尚未解析。`);
      if (meta.status !== 'ready') throw new Error(`文件《${meta.name}》当前状态为 ${meta.status}。`);
      return { meta, indexes };
    }));
  }

  createEvalModel(meta, indexes, selector = {}, state = {}) {
    const model = {
      id: meta.id,
      name: meta.name,
      kind: meta.kind,
      metadata: {
        file_id: meta.id,
        name: meta.name,
        mime: meta.mime,
        extension: meta.extension,
        size: meta.size,
        stats: meta.stats || {}
      },
      text: '',
      lines: [],
      rows: [],
      columns: [],
      sheets: [],
      pages: [],
      paragraphs: [],
      tables: Array.isArray(indexes.tables) ? indexes.tables : [],
      image: meta.kind === 'image' ? { width: meta.stats && meta.stats.width || 0, height: meta.stats && meta.stats.height || 0 } : null,
      capabilities: meta.capabilities || []
    };
    const maxRows = Math.max(1, Math.min(Number(selector.max_rows) || MAX_EVAL_ROWS, MAX_EVAL_ROWS));
    const addRows = rows => {
      const result = [];
      for (const row of (Array.isArray(rows) ? rows : [])) {
        if (result.length >= maxRows || state.cells >= MAX_EVAL_CELLS) {
          state.truncated = true;
          break;
        }
        const values = Array.isArray(row) ? row.slice(0, MAX_EVAL_COLUMNS) : [row];
        state.cells += values.length;
        result.push(values);
      }
      if (Array.isArray(rows) && rows.length > result.length) state.truncated = true;
      return result;
    };

    if (meta.kind === 'spreadsheet') {
      model.sheets = (indexes.sheets || []).map(sheet => ({ name: sheet.name, rows: addRows(sheet.rows) }));
      const selected = selector.sheet
        ? model.sheets.find(sheet => sheet.name === selector.sheet)
        : model.sheets[0];
      if (selector.sheet && !selected) throw new Error(`找不到 Sheet《${selector.sheet}》。`);
      model.rows = selected ? (selector.range ? this.readSheetRange(selected.rows, selector.range, 0, maxRows) : selected.rows) : [];
      model.columns = model.rows[0] || [];
    } else if (meta.kind === 'csv') {
      model.rows = addRows(indexes.rows || []);
      model.sheets = [{ name: 'Sheet1', rows: model.rows }];
      model.columns = model.rows[0] || [];
    }

    if (typeof indexes.text === 'string' && !Array.isArray(indexes.pages) && state.textChars < MAX_EVAL_TEXT_CHARS) {
      const remaining = MAX_EVAL_TEXT_CHARS - state.textChars;
      model.text = indexes.text.slice(0, remaining);
      state.textChars += model.text.length;
      if (model.text.length < indexes.text.length) state.truncated = true;
      model.lines = model.text.split('\n');
    }
    if (Array.isArray(indexes.pages)) {
      const start = Math.max(0, Number(selector.start_page || 1) - 1);
      const end = Math.min(indexes.pages.length, Number(selector.end_page || indexes.pages.length));
      model.pages = indexes.pages.slice(start, end);
      const pageText = model.pages.join('\n\n');
      const remaining = Math.max(0, MAX_EVAL_TEXT_CHARS - state.textChars);
      model.text = pageText.slice(0, remaining);
      state.textChars += model.text.length;
      if (model.text.length < pageText.length) state.truncated = true;
      model.lines = model.text.split('\n');
    }
    model.paragraphs = Array.isArray(indexes.paragraphs)
      ? indexes.paragraphs.slice(0, maxRows)
      : (model.text ? model.text.split(/\n{2,}/).filter(Boolean).slice(0, maxRows) : []);
    return model;
  }

  async inspectFiles(args = {}, context = {}) {
    const resolved = await this.resolveToolFiles(args.file_ids, context);
    const detail = ['metadata', 'schema', 'outline'].includes(args.detail) ? args.detail : 'metadata';
    const documents = resolved.map(({ meta, indexes }) => {
      const document = {
        file_id: meta.id,
        name: meta.name,
        kind: meta.kind,
        mime: meta.mime,
        size: meta.size,
        capabilities: meta.capabilities || [],
        stats: meta.stats || {}
      };
      if (detail !== 'metadata') {
        if (meta.kind === 'spreadsheet') {
          document.sheets = (indexes.sheets || []).slice(0, 100).map(sheet => ({
            name: sheet.name,
            rows: Array.isArray(sheet.rows) ? sheet.rows.length : 0,
            columns: Array.isArray(sheet.rows) && sheet.rows[0] ? sheet.rows[0].length : 0,
            headers: Array.isArray(sheet.rows) && sheet.rows[0] ? sheet.rows[0].slice(0, 50) : []
          }));
        } else if (meta.kind === 'csv') {
          document.columns = indexes.columns || (indexes.rows && indexes.rows[0]) || [];
          document.rows = indexes.rows ? indexes.rows.length : 0;
        } else if (Array.isArray(indexes.pages)) {
          document.pages = indexes.pages.length;
        } else {
          document.lines = indexes.lineOffsets ? indexes.lineOffsets.length : 0;
          document.paragraphs = indexes.paragraphs ? indexes.paragraphs.length : 0;
        }
      }
      if (detail === 'outline') document.preview = String(meta.preview || '').slice(0, 600);
      return document;
    });
    return this.limitToolResult(JSON.stringify({ tool_name: 'workspace_inspect', file_ids: documents.map(file => file.file_id), documents }));
  }

  async evaluateWorkspace(args = {}, context = {}) {
    const resolved = await this.resolveToolFiles(args.file_ids || args.file_id, context);
    const state = { cells: 0, textChars: 0, truncated: false };
    const selector = args.selector && typeof args.selector === 'object' ? args.selector : {};
    const documents = resolved.map(({ meta, indexes }) => this.createEvalModel(meta, indexes, selector, state));
    const firstDocument = documents[0] || {};
    const sheets = Array.isArray(firstDocument.sheets)
      ? firstDocument.sheets.reduce((result, sheet) => {
        if (sheet && sheet.name) result[sheet.name] = sheet.rows || [];
        return result;
      }, {})
      : {};
    const value = await executeWorkspaceCode(args.code, {
      document: documents[0],
      documents,
      metadata: documents.map(document => document.metadata),
      rows: firstDocument.rows || [],
      sheets
    });
    const outputValue = args.output === 'text' && typeof value !== 'string'
      ? JSON.stringify(value, null, 2)
      : value;
    return this.limitToolResult(JSON.stringify({
      tool_name: args._toolName || 'workspace_eval',
      file_ids: documents.map(document => document.id),
      input_truncated: state.truncated,
      result: outputValue,
      warnings: state.truncated ? ['输入数据已达到本次分析上限。'] : []
    }));
  }

  async transformWorkspace(args = {}, context = {}) {
    if (!args.output || !args.output.filename || !args.output.format) throw new Error('文件输出需要 filename 和 format。');
    if (args.overwrite && !context.allowOverwrite) throw new Error('覆盖工作区文件需要用户确认。');
    const result = await this.evaluateWorkspace({ ...args, output: 'json' }, context);
    const parsed = JSON.parse(result);
    const format = String(args.output.format).toLowerCase();
    const allowed = new Set(['json', 'csv', 'xlsx', 'md', 'txt', 'docx']);
    if (!allowed.has(format)) throw new Error(`不支持生成 ${format} 文件。`);
    let content = parsed.result;
    if (format === 'json' || format === 'xlsx' || format === 'csv') content = JSON.stringify(content == null ? [] : content);
    else if (typeof content !== 'string') content = JSON.stringify(content == null ? '' : content, null, 2);
    const mimeByFormat = {
      json: 'application/json', csv: 'text/csv;charset=utf-8', xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      md: 'text/markdown;charset=utf-8', txt: 'text/plain;charset=utf-8', docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    };
    const existing = args.overwrite
      ? (await this.listFiles(context.sessionId)).find(file => file.name === sanitizeFileName(args.output.filename) && file.workspaceId === sessionWorkspaceId(context.sessionId))
      : null;
    const meta = await this.writeFile({ filename: args.output.filename, mime: mimeByFormat[format], content }, {
      ...context,
      sourceFileIds: parsed.file_ids,
      operation: 'workspace_transform'
    });
    if (existing) await this.deleteFile(existing.id);
    return this.limitToolResult(JSON.stringify({
      tool_name: 'workspace_transform',
      file_ids: parsed.file_ids,
      input_truncated: !!parsed.input_truncated,
      result: { file_id: meta.id, name: meta.name, size: meta.size, created: true, overwritten_file_id: existing ? existing.id : '' },
      warnings: parsed.warnings || []
    }));
  }

  async extractWorkspace(args = {}, context = {}) {
    if (!args.schema || typeof args.schema !== 'object') throw new Error('结构化提取需要 schema。');
    const resolved = await this.resolveToolFiles(args.file_ids || args.file_id, context);
    const state = { cells: 0, textChars: 0, truncated: false };
    const documents = resolved.map(({ meta, indexes }) => this.createEvalModel(meta, indexes, {}, state));
    const coerce = (value, type) => {
      if (value === undefined || value === null || value === '') return value;
      if (type === 'number') {
        const number = Number(String(value).replace(/[,，]/g, ''));
        return Number.isFinite(number) ? number : value;
      }
      if (type === 'boolean') return /^(true|yes|是|1)$/i.test(String(value).trim());
      if (type === 'array') return Array.isArray(value) ? value : String(value).split(/[,，、]/).map(item => item.trim()).filter(Boolean);
      return value;
    };
    const extract = document => {
      const keys = Object.keys(args.schema);
      const headers = Array.isArray(document.rows && document.rows[0]) ? document.rows[0] : [];
      const headerIndexes = keys.map(key => headers.findIndex(value => String(value).trim().toLowerCase() === key.toLowerCase()));
      if (headers.length && headerIndexes.every(index => index >= 0)) {
        return document.rows.slice(1).map(row => keys.reduce((result, key, index) => {
          result[key] = coerce(row[headerIndexes[index]], args.schema[key]);
          return result;
        }, {}));
      }
      return keys.reduce((result, key) => {
      const headerIndex = Array.isArray(document.rows && document.rows[0])
        ? document.rows[0].findIndex(value => String(value).trim().toLowerCase() === key.toLowerCase())
        : -1;
      if (headerIndex >= 0 && document.rows.length > 1) result[key] = coerce(document.rows[1][headerIndex], args.schema[key]);
      else {
        const match = new RegExp(`${key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*[:：]\\s*(.+)`, 'i').exec(document.text || '');
        if (match) result[key] = coerce(match[1].trim(), args.schema[key]);
      }
      return result;
      }, {});
    };
    return this.limitToolResult(JSON.stringify({
      tool_name: 'workspace_extract',
      file_ids: documents.map(document => document.id),
      result: documents.map(document => ({ file_id: document.id, data: extract(document) })),
      input_truncated: state.truncated,
      warnings: ['基础字段提取按列名或“字段：值”文本匹配；复杂语义字段请先使用 workspace_read 或 workspace_eval。']
    }));
  }

  async compareWorkspace(args = {}, context = {}) {
    if (!args.left_file_id || !args.right_file_id) throw new Error('文件比较需要 left_file_id 和 right_file_id。');
    const resolved = await this.resolveToolFiles([args.left_file_id, args.right_file_id], context);
    const [left, right] = resolved;
    const mode = ['content', 'structure', 'table'].includes(args.mode) ? args.mode : 'content';
    const leftModel = this.createEvalModel(left.meta, left.indexes, {}, { cells: 0, textChars: 0, truncated: false });
    const rightModel = this.createEvalModel(right.meta, right.indexes, {}, { cells: 0, textChars: 0, truncated: false });
    const leftValues = mode === 'table' ? leftModel.rows : (mode === 'structure' ? [leftModel.kind, leftModel.metadata, leftModel.sheets.map(sheet => sheet.name)] : leftModel.text.split('\n'));
    const rightValues = mode === 'table' ? rightModel.rows : (mode === 'structure' ? [rightModel.kind, rightModel.metadata, rightModel.sheets.map(sheet => sheet.name)] : rightModel.text.split('\n'));
    const changes = [];
    const length = Math.max(leftValues.length, rightValues.length);
    for (let index = 0; index < length && changes.length < 200; index += 1) {
      const leftValue = JSON.stringify(leftValues[index] === undefined ? null : leftValues[index]);
      const rightValue = JSON.stringify(rightValues[index] === undefined ? null : rightValues[index]);
      if (leftValue !== rightValue) changes.push({ index, left: leftValues[index] === undefined ? null : leftValues[index], right: rightValues[index] === undefined ? null : rightValues[index] });
    }
    return this.limitToolResult(JSON.stringify({
      tool_name: 'workspace_compare',
      file_ids: [left.meta.id, right.meta.id],
      mode,
      summary: { left_count: leftValues.length, right_count: rightValues.length, changed: changes.length, truncated: length > changes.length && changes.length >= 200 },
      changes
    }));
  }

  async requestFull({ file_id: id, format = 'text' } = {}, context = {}) {
    const meta = context.sessionId
      ? await this.getAccessibleFile(id, context.sessionId)
      : await this.getFile(id);
    if (!meta) throw new Error('文件不存在。');
    if (format === 'binary' || meta.kind === 'image') {
      return JSON.stringify({ file_id: id, requires_user_confirmation: true, kind: meta.kind, size: meta.size });
    }
    const indexes = await this.getIndexes(id);
    return this.limitToolResult(indexes && indexes.text ? indexes.text : '', true);
  }

  limitToolResult(content, allowLarge = false) {
    const text = String(content || '');
    const limit = allowLarge ? MAX_TOOL_RESULT_CHARS : Math.min(MAX_TOOL_RESULT_CHARS, 256 * 1024);
    if (text.length <= limit) return text;
    return `${text.slice(0, limit)}\n[Workspace result truncated at ${limit} characters]`;
  }

  async writeFile({ filename, mime = '', content = '' }, { sessionId, sourceFileIds = [], operation = '' } = {}) {
    const safeName = sanitizeFileName(filename || 'generated.txt');
    const blob = await generateWorkspaceBlob(safeName, mime, content);
    const file = new File([blob], safeName, { type: blob.type || mime || 'application/octet-stream' });
    return this.addFile(file, { sessionId, source: 'model', sourceFileIds, operation });
  }

  async downloadFile(id) {
    const [meta, blob] = await Promise.all([this.getFile(id), this.getBlob(id)]);
    if (!meta || !blob) throw new Error('文件内容不存在。');
    if (/Android/i.test(navigator.userAgent) && blob.size <= 2 * 1024 * 1024) {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
      });
      const params = new URLSearchParams({
        name: meta.name,
        mime: meta.mime || blob.type || '',
        base64: String(dataUrl).split(',')[1] || ''
      });
      const frame = document.createElement('iframe');
      frame.style.display = 'none';
      frame.src = `aihelp://saveFile?${params.toString()}`;
      document.body.appendChild(frame);
      setTimeout(() => frame.remove(), 1000);
      return;
    }
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = meta.name;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  async buildManifest(sessionId) {
    const files = await this.listFiles(sessionId);
    return files.map(file => ({
      id: file.id,
      name: file.name,
      kind: file.kind,
      mime: file.mime,
      size: file.size,
      status: file.status,
      preview: file.preview,
      stats: file.stats,
      capabilities: file.capabilities,
      pinned: file.pinned,
      document_model_version: file.documentModelVersion || DOCUMENT_MODEL_VERSION,
      source: file.source,
      source_file_ids: file.sourceFileIds || [],
      operation: file.operation || '',
      version: file.version || 1
    }));
  }

  async executeTool(name, args, context = {}) {
    if (name === 'workspace_inspect') return this.inspectFiles(args, context);
    if (name === 'workspace_read') return this.readFile(args, context);
    if (name === 'workspace_search') return this.search(args, context);
    if (name === 'workspace_excel_eval') {
      return this.evaluateWorkspace({
        ...args,
        _toolName: 'workspace_excel_eval',
        file_ids: args.file_ids || [args.file_id],
        selector: { ...(args.selector || {}), sheet: args.sheet, max_rows: args.max_rows }
      }, context);
    }
    if (name === 'workspace_eval') return this.evaluateWorkspace(args, context);
    if (name === 'workspace_transform') return this.transformWorkspace(args, context);
    if (name === 'workspace_extract') return this.extractWorkspace(args, context);
    if (name === 'workspace_compare') return this.compareWorkspace(args, context);
    if (name === 'workspace_request_full') return this.requestFull(args, context);
    if (name === 'workspace_attach') {
      const meta = context.sessionId
        ? await this.getAccessibleFile(args.file_id, context.sessionId)
        : await this.getFile(args.file_id);
      if (!meta) throw new Error('文件不存在。');
      return JSON.stringify({ file_id: meta.id, requires_user_confirmation: true, kind: meta.kind, purpose: args.purpose || '' });
    }
    if (name === 'workspace_write') {
      const meta = await this.writeFile(args, context);
      return JSON.stringify({ file_id: meta.id, name: meta.name, size: meta.size, created: true });
    }
    throw new Error(`不支持的工作区工具：${name}`);
  }
}

export const workspaceService = new WorkspaceService();
export function shouldEnableWorkspaceTools(prompt = '', manifest = [], toolResults = []) {
  return (Array.isArray(manifest) && manifest.length > 0) ||
    // session_memory_* results share this channel but don't need workspace tools
    (Array.isArray(toolResults) && toolResults.some(result => result && String(result.name || '').startsWith('workspace_'))) ||
    WORKSPACE_INTENT_PATTERN.test(String(prompt || ''));
}

export { sessionWorkspaceId, sanitizeFileName, MAX_FILE_BYTES, MAX_WORKSPACE_BYTES };
